/**
 * CLI documentation page content.
 *
 * Covers installation, authentication, teammate management,
 * task execution, interactive chat, and development mode.
 */

export const cliContent = `
The \`m8tes\` CLI lets you manage teammates and run tasks directly from your terminal.

## Installation

\`\`\`bash
pip install m8tes
\`\`\`

Verify the install:

\`\`\`bash
m8tes --version
\`\`\`

## Authentication

Log in to save your credentials locally:

\`\`\`bash
m8tes auth login
\`\`\`

You'll be prompted for your email and password. Credentials are stored securely for future sessions.

Other auth commands:

\`\`\`bash
m8tes auth status    # Check current login
m8tes auth logout    # Clear saved credentials
m8tes auth register  # Create a new account
\`\`\`

Or skip login and pass your API key directly:

\`\`\`bash
m8tes --api-key m8_your_key_here mate task "Do something"
\`\`\`

## Running Tasks

Execute a one-off task with a teammate:

\`\`\`bash
# Auto-detect teammate (if you only have one)
m8tes mate task "Summarize the latest AI news"

# Specify teammate by ID
m8tes mate task 7 "Prepare the Weekly Report"
\`\`\`

### Output Formats

\`\`\`bash
m8tes mate task "Do X" --output verbose   # Rich UI with tool calls (default)
m8tes mate task "Do X" --output compact   # Text-only output
m8tes mate task "Do X" --output json      # Raw event JSON
m8tes mate task "Do X" --debug            # Show event traces and counts
\`\`\`

## Interactive Chat

Start a multi-turn conversation:

\`\`\`bash
m8tes mate chat              # Auto-detect teammate
m8tes mate chat 7            # Specify teammate ID
m8tes mate chat --resume 42  # Resume from a previous run
\`\`\`

Session commands:

| Command | Description |
|---------|-------------|
| \`/exit\` or \`/quit\` | End the chat session |
| \`/clear\` | Clear conversation history |
| \`/resume <run_id>\` | Switch to a different run's context |

## Teammate Management

\`\`\`bash
m8tes mate list                          # List all teammates
m8tes mate list --include-disabled       # Include disabled teammates
m8tes mate get 7                         # Show teammate details
m8tes mate create                        # Interactive creation wizard
m8tes mate create --non-interactive \\
  --name "Ops Bot" \\
  --tools gmail slack \\
  --instructions "Handle ops tasks"      # Scripted creation
m8tes mate update 7                      # Interactive update
m8tes mate update 7 --non-interactive \\
  --name "New Name"                      # Scripted update
m8tes mate enable 7                      # Re-enable a disabled teammate
m8tes mate disable 7                     # Soft-disable (preserves history)
m8tes mate archive 7                     # Archive (hidden from listings)
\`\`\`

## Task Management

Create reusable task definitions attached to teammates. Tasks can be triggered manually, on a schedule, via webhook, or by email.

\`\`\`bash
m8tes task list                           # List all tasks
m8tes task list --mate-id 7              # Filter by teammate
m8tes task get 42                        # Show task details
m8tes task create                        # Interactive creation wizard
m8tes task create --non-interactive \\
  --mate-id 7 --name "Weekly Report" \\
  --instructions "Generate metrics"      # Scripted creation
m8tes task execute 42                    # Run a saved task
m8tes task update 42 --name "New Name"   # Update task fields
m8tes task enable 42                     # Re-enable a disabled task
m8tes task disable 42                    # Disable a task
m8tes task archive 42                    # Archive a task
\`\`\`

## Run Inspection

View details about past runs — conversation history, token usage, and tool executions.

\`\`\`bash
m8tes run list                    # List recent runs
m8tes run list-mate 7             # Runs for a specific teammate
m8tes run get 123                 # Full run details
m8tes run conversation 123        # View chat messages
m8tes run usage 123               # Token usage and costs
m8tes run tools 123               # Tool executions
m8tes run tools 123 --verbose     # With arguments and results
\`\`\`

## Development Mode

Use \`--dev\` to target your local backend:

\`\`\`bash
m8tes --dev auth login
m8tes --dev mate task "Test locally"
\`\`\`

This connects to \`http://127.0.0.1:8000\` by default. Override the port with \`M8TES_PORT\`:

\`\`\`bash
export M8TES_PORT=9000
m8tes --dev mate task "Uses port 9000"
\`\`\`

Or point to any backend with \`--base-url\`:

\`\`\`bash
m8tes --base-url https://staging.m8tes.ai mate task "Test staging"
\`\`\`

## Command Aliases

Most commands have short aliases for faster typing:

| Full Command | Alias |
|-------------|-------|
| \`m8tes mate\` | \`m8tes m\` |
| \`m8tes mate task\` | \`m8tes m t\` |
| \`m8tes mate chat\` | \`m8tes m ch\` |
| \`m8tes mate list\` | \`m8tes m ls\` |
| \`m8tes mate create\` | \`m8tes m c\` |
| \`m8tes task execute\` | \`m8tes task x\` |
| \`m8tes task list\` | \`m8tes task ls\` |
| \`m8tes run list\` | \`m8tes r ls\` |
| \`m8tes run list-mate\` | \`m8tes r lm\` |
| \`m8tes auth\` | \`m8tes a\` |
| \`m8tes auth login\` | \`m8tes a l\` |

## CLI vs SDK

The CLI uses the same backend as the [Python SDK](/docs/quickstart). Choose based on your workflow:

| | CLI | SDK |
|---|---|---|
| **Best for** | Quick tasks, debugging, exploration | Applications, automation, integrations |
| **Auth** | Email/password login | API key (\`m8_\`) |
| **Streaming** | Built-in terminal output | \`RunStream\` iterator |
`;
